import { ChipProps } from "@mui/material/Chip";
import {
  PricebookPendingSchedule,
  PricebookSchedule,
  PricebookScheduleConflictReason,
  PricebookScheduleStatus,
} from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import { formatDateTimeBkk } from "../../../../utils/date";
import { formatPeriodDate, formatShortDate } from "../Periods/periodModel";

const SEPARATOR = " · ";
const NO_NAME = "-";

export const SCHEDULE_STATUS_LABEL: Record<PricebookScheduleStatus, string> = {
  pending: "รอเวลา",
  applied: "ทำแล้ว",
  cancelled: "ยกเลิก",
  conflict: "ชนกัน",
};

export const SCHEDULE_STATUS_COLOR: Record<
  PricebookScheduleStatus,
  ChipProps["color"]
> = {
  pending: "info",
  applied: "success",
  cancelled: "default",
  conflict: "error",
};

/** Why the job left a schedule unapplied; the API sends only the reason code. */
export const SCHEDULE_CONFLICT_TEXT: Record<PricebookScheduleConflictReason, string> = {
  source_changed: "สมุดต้นฉบับถูกแก้หลังตั้งเวลา ระบบจึงไม่นำไปใช้",
  source_deleted: "สมุดต้นฉบับถูกลบ",
};

/** Only a schedule still waiting for its day can be cancelled. */
export const isSchedulePending = (schedule: Pick<PricebookSchedule, "status">): boolean =>
  schedule.status === "pending";

/** The list chip of a book with a schedule waiting: "ตั้งเวลา 01/11". */
export const formatScheduleChipLabel = (effectiveDate: string): string =>
  `ตั้งเวลา ${formatShortDate(effectiveDate)}`;

/** "มีผล 01/11/2026 เวลา 00:00" — the job applies a schedule from the start of its Bangkok day. */
export const formatScheduleEffectiveText = (effectiveDate: string): string =>
  `มีผล ${formatPeriodDate(effectiveDate)} เวลา 00:00`;

/** Who set the schedule and when, then what became of it. */
export const buildScheduleAuditLine = (schedule: PricebookSchedule): string => {
  const parts = [
    `ตั้งโดย ${schedule.created_by_name ?? NO_NAME} เมื่อ ${formatDateTimeBkk(schedule.created_at)}`,
  ];
  if (schedule.applied_at) {
    parts.push(`นำไปใช้เมื่อ ${formatDateTimeBkk(schedule.applied_at)}`);
  }
  if (schedule.cancelled_at) {
    parts.push(
      `ยกเลิกโดย ${schedule.cancelled_by_name ?? NO_NAME} เมื่อ ${formatDateTimeBkk(schedule.cancelled_at)}`,
    );
  }
  return parts.join(SEPARATOR);
};

/**
 * The header bar of a book with schedules waiting: the soonest one (the API sends them soonest
 * first) and, when there are more, how many.
 */
export const buildScheduleBarText = (pending: PricebookPendingSchedule[]): string => {
  const [next] = pending;
  if (!next) return "";
  const nextText = `ราคาจากร่าง ${next.draft_code} ${formatScheduleEffectiveText(next.effective_date)}`;
  if (pending.length === 1) {
    return `ตั้งเวลาไว้: ${nextText} (ตั้งโดย ${next.created_by_name ?? NO_NAME})`;
  }
  return `ตั้งเวลาไว้ ${numberWithCommas(pending.length)} รายการ${SEPARATOR}รายการถัดไป ${nextText}`;
};
